import 'hammer-timejs';
import 'hammerjs';

import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpModule } from '@angular/http';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from '@appShared/shared.module';
import { ToastrModule } from 'ngx-toastr';
import * as Sentry from '@sentry/browser';

import { environment } from '../environments/environment';
import { Collapse } from './../app/directives/collapse.directive';
import { GoogleAnalyticsEventsService } from './../app/services/google-analytics-events.service';
import { HelpService } from './../app/services/help.service';
import { OfflineQuestionnaireService } from './../app/services/offline-questionnaire.service';
import { Globals } from './../globals';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
import { BaseConfirmPasswordFormComponent } from './components/_shared/account/base-confirm-password-form/base-confirm-password-form.component';
import { BaseManagePasswordComponent } from './components/_shared/account/base-manage-password/base-manage-password.component';
import { BreadcrumbComponent } from './components/_shared/breadcrumb/breadcrumb.component';
import { ErrorComponent } from './components/_shared/error/error.component';
import { InteractiveMapComponent } from './components/_shared/interactive-map/interactive-map.component';
import {
  LoadingProvider,
  LoadingSpinnerComponent
} from './components/_shared/loading-spinner/loading-spinner.component';
import { MainLayoutComponent } from './components/_shared/main-layout/main-layout.component';
import { MainNavComponent } from './components/_shared/main-nav/main-nav.component';
import { MainAccountLinksComponent } from './components/_shared/main-account-links/main-account-links.component';
import { ModalComponent, ModalProvider } from './components/_shared/modal/modal.component';
import { RatingBarComponent } from './components/_shared/rating-bar/rating-bar.component';
import { VerticalProgressIndicatorComponent } from './components/_shared/vertical-progress-indicator/vertical-progress-indicator.component';
import { ActivationComponent } from './components/account/activation/activation.component';
import { ChangePasswordComponent } from './components/account/change-password/change-password.component';
import { ForgottenLoginComponent } from './components/account/forgotten-login/forgotten-login.component';
import { LoginComponent } from './components/account/login/login.component';
import { RegisterCompleteComponent } from './components/account/register-complete/register-complete.component';
import { RegisterComponent } from './components/account/register/register.component';
import { RequestPaymentComponent } from './components/account/request-payment/request-payment.component';
import { ResendActivationComponent } from './components/account/resend-activation/resend-activation.component';
import { ResetPasswordComponent } from './components/account/reset-password/reset-password.component';
import { RightOfAccessComponent } from './components/account/right-of-access/right-of-access.component';
import { RightToErasureComponent } from './components/account/right-to-erasure/right-to-erasure.component';
import { AccountSettingsComponent } from './components/account/settings/account-settings.component';
import { CourseAreaDetailComponent } from './components/course-areas/detail/course-area-detail/course-area-detail.component';
import { CourseAreaApplyFiltersComponent } from './components/course-areas/filters/course-area-apply-filters/course-area-apply-filters.component';
import { CourseAreaBudgetFilterComponent } from './components/course-areas/filters/course-area-budget-filter/course-area-budget-filter.component';
import { CourseAreaCoursesFilterComponent } from './components/course-areas/filters/course-area-courses-filter/course-area-courses-filter.component';
import { CourseAreaLanguagesFilterComponent } from './components/course-areas/filters/course-area-languages-filter/course-area-languages-filter.component';
import { CourseAreaLocationFilterComponent } from './components/course-areas/filters/course-area-location-filter/course-area-location-filter.component';
import { CourseAreaPointsFilterComponent } from './components/course-areas/filters/course-area-points-filter/course-area-points-filter.component';
import { CourseAreaListComponent } from './components/course-areas/list/course-area-list/course-area-list.component';
import { CourseAreaFilterPipe } from './components/course-areas/pipes/course-area.pipe';
import { FilterValuesPipe } from './components/course-areas/pipes/filter-values.pipe';
import { ExportComponent } from './components/finalise/export/export.component';
import { FinaliseListComponent } from './components/finalise/list/finalise-list.component';
import { HelpComponent } from './components/help/help/help.component';
import { PrivacyPolicyComponent } from './components/legal/privacy-policy/privacy-policy.component';
import { PrivacyPolicyUpdateComponent } from '@app/components/legal/privacy-policy-update/privacy-policy-update.component';
import { OptionsPipe } from './components/questionnaire/pipes/options.pipe';
import { QuestionnaireCompleteComponent } from './components/questionnaire/questionnaire-complete/questionnaire-complete.component';
import { QuestionnaireComponent } from './components/questionnaire/questionnaire/questionnaire.component';
import { FreeTextComponent } from './components/questionnaire/questions/free-text/free-text.component';
import { MultiChoiceComponent } from './components/questionnaire/questions/multi-choice/multi-choice.component';
import { MultiSelectComponent } from './components/questionnaire/questions/multi-select/multi-select.component';
import { StatementComponent } from './components/questionnaire/questions/statement/statement.component';
import { ReviewQuestionsComponent } from './components/questionnaire/review-questions/review-questions.component';
import { WelcomeComponent } from './components/welcome/welcome.component';
import { EqualsValidatorDirective } from './directives/equals-validator.directive';
import { LocaleDirective } from './directives/locale.directive';
import { PasswordValidatorDirective } from './directives/password-validator.directive';
import { httpInterceptorProviders } from './http-interceptors';
import { CourseListFilterPipe } from './pipes/courses/course-list-filter.pipe';
import { AuthService } from './services/auth.service';
import { ChecklistService } from './services/checklist.service';
import { ConnectionService } from './services/connection.service';
import { CourseService } from './services/course.service';
import { CurrentQuestionnaireService } from './services/current-questionnaire.service';
import { ErrorService } from './services/error.service';
import { Footer } from './services/footer.service';
import { AuthGuardService } from './services/guards/auth-guard.service';
import { CanDeactivateGuard } from './services/guards/can-deactivate-guard.service';
import { localisationFactory, LocalisationService } from './services/localisation.service';
import { NavigationService } from './services/navigation.service';
import { QuestionnaireService } from './services/questionnaire.service';
import { ResearchService } from './services/research.service';
import { ResetPasswordService } from './services/reset-password.service';
import { RouteBreadcrumbs } from './services/routeBreadcrumbs.service';
import { RowColumnSorter } from './services/row-column-sorter.service';
import { UserService } from './services/user.service';
import { SortablejsModule } from 'angular-sortablejs';
import { TagInputModule } from 'ngx-chips';
import { ScreenSizeService } from '@app/services/screen-size.service';
import { CanActivateResearchService } from './services/guards/can-activate-research.service';
import { CanRegisterService } from './services/guards/can-register.service';
import { NgxBalanceTextModule } from 'ngx-balance-text';
import { BtnDropdownComponent } from '@appShared/components/btn-dropdown/btn-dropdown.component';
import { ChecklistComponent } from '@app/components/_shared/checklist/checklist.component';

Sentry.init({
  dsn: AppConfig.sentryDsn,
  release: AppConfig.version
});

export class SentryErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    Sentry.withScope(scope => {
      scope.setExtra('debug', false);
      Sentry.captureException(err.originalError || err);
    });
    if (!environment.production) {
      console.error(err);
    }
  }
}

export class MyHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    swipe: { velocity: 0.4, threshold: 20 },
    pinch: { enable: false },
    rotate: { enable: false }
  };
}

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-full-width',
      preventDuplicates: false,
      disableTimeOut: false,
      maxOpened: 2,
      autoDismiss: true
    }),
    SortablejsModule.forRoot({ animation: 150 }),
    TagInputModule,
    NgxBalanceTextModule
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: localisationFactory,
      deps: [LocalisationService],
      multi: true
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig
    },
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler
    },
    // { provide: LocationStrategy, useClass: HashLocationStrategy },
    AuthService,
    AuthGuardService,
    CanDeactivateGuard,
    CanRegisterService,
    CourseService,
    QuestionnaireService,
    UserService,
    ResearchService,
    ChecklistService,
    ScreenSizeService,
    ConnectionService,
    CurrentQuestionnaireService,
    NavigationService,
    ErrorService,
    ModalProvider,
    LoadingProvider,
    RouteBreadcrumbs,
    Footer,
    LocalisationService,
    RowColumnSorter,
    CanActivateResearchService,
    GoogleAnalyticsEventsService,
    OfflineQuestionnaireService,
    HelpService,
    ResetPasswordService,
    Globals,
    httpInterceptorProviders
  ],
  declarations: [
    AppComponent,
    ErrorComponent,
    LoginComponent,
    RegisterComponent,
    WelcomeComponent,
    ModalComponent,
    RegisterCompleteComponent,
    MainNavComponent,
    MainAccountLinksComponent,
    MainLayoutComponent,
    QuestionnaireComponent,
    MultiChoiceComponent,
    FreeTextComponent,
    MultiSelectComponent,
    QuestionnaireCompleteComponent,
    StatementComponent,
    CourseAreaListComponent,
    CourseAreaDetailComponent,
    CourseAreaApplyFiltersComponent,
    CourseAreaLocationFilterComponent,
    CourseAreaCoursesFilterComponent,
    CourseAreaBudgetFilterComponent,
    CourseAreaLanguagesFilterComponent,
    CourseAreaPointsFilterComponent,
    OptionsPipe,
    CourseAreaFilterPipe,
    ActivationComponent,
    ForgottenLoginComponent,
    ResendActivationComponent,
    ResetPasswordComponent,
    PasswordValidatorDirective,
    LoadingSpinnerComponent,
    VerticalProgressIndicatorComponent,
    ReviewQuestionsComponent,
    EqualsValidatorDirective,
    BreadcrumbComponent,
    FilterValuesPipe,
    CourseListFilterPipe,
    AccountSettingsComponent,
    RatingBarComponent,
    ExportComponent,
    FinaliseListComponent,
    LocaleDirective,
    PrivacyPolicyComponent,
    PrivacyPolicyUpdateComponent,
    HelpComponent,
    Collapse,
    BaseManagePasswordComponent,
    ChangePasswordComponent,
    RightOfAccessComponent,
    RightToErasureComponent,
    BaseConfirmPasswordFormComponent,
    InteractiveMapComponent,
    RequestPaymentComponent,
    BtnDropdownComponent,
    ChecklistComponent
  ],
  entryComponents: [ModalComponent],
  bootstrap: [AppComponent]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts